import React from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { UserMiniCard } from './UserMiniCard';
import type { FloorRow } from '../../utils/floor';
import type { AgentRow } from '../../types/voip';
export interface RosterMetric {
  label: string;
  value: string;
  tone?: string;
  delta?: React.ReactNode;
}
interface RosterGroupProps {
  id: string;
  title: string;
  countLabel: string;
  badge: React.ReactNode;
  metrics: RosterMetric[];
  users: AgentRow[];
  expanded: boolean;
  showingAll: boolean;
  searching: boolean;
  matchedIds: Set<string>;
  floorById: Map<string, FloorRow>;
  onToggle: (id: string) => void;
  onShowAll: (id: string, value: boolean) => void;
  onViewAll?: () => void;
  viewAllLabel?: string;
  onSelect: (id: string) => void;
}
const PREVIEW = 4;
const ease = [0.23, 1, 0.32, 1] as const;

/** Collapsible roster section: summary header, top-user preview, expandable grid */
export function RosterGroup({
  id,
  title,
  countLabel,
  badge,
  metrics,
  users,
  expanded,
  showingAll,
  searching,
  matchedIds,
  floorById,
  onToggle,
  onShowAll,
  onViewAll,
  viewAllLabel = 'View team',
  onSelect
}: RosterGroupProps) {
  const visible = showingAll ? users : users.slice(0, PREVIEW);
  const more = users.length - visible.length;
  const panelId = `roster-panel-${id}`;
  const canShowFewer = showingAll && !searching && users.length > PREVIEW;
  return <section aria-labelledby={`roster-${id}-name`} className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card" data-id="element-625" data-mp="id7nkr93wltq">
      <h2 className="m-0" data-id="element-626" data-mp="df65a8ji8qjb">
        <button type="button" onClick={() => onToggle(id)} aria-expanded={expanded} aria-controls={panelId} className="flex w-full flex-wrap items-center gap-x-8 gap-y-3 px-5 py-4 text-left transition-colors duration-150 ease-out hover:bg-canvas/50 focus:outline-none focus-visible:bg-canvas sm:px-6 lg:flex-nowrap" data-id="element-627" data-mp="dgh9uakry2bh">
          <span className="flex min-w-0 items-center gap-3 lg:w-60 lg:shrink-0" data-id="element-628" data-mp="12vb5qo41y2r">
            {badge}
            <span className="min-w-0" data-id="element-629" data-mp="5wg24yradrsg">
              <span id={`roster-${id}-name`} className="block truncate text-[15px] font-semibold text-ink" data-id="element-630" data-mp="3o9e7jl6ulcu0">
                {title}
              </span>
              <span className="block text-xs text-muted" data-id="element-631" data-mp="3sb95qgthfa3k">{countLabel}</span>
            </span>
          </span>

          <span className="order-3 grid w-full grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-5 lg:order-none lg:w-auto lg:flex-1" data-id="element-632" data-mp="jvplu8epb416">
            {metrics.map(m => <span key={m.label} className="block" data-id="element-633" data-mp="at6izoax0rex">
                <span className="flex items-baseline gap-1.5" data-id="element-634" data-mp="awkoh5evlgql">
                  <span className={`text-[15px] font-bold leading-tight ${m.tone ?? 'text-ink'}`} data-id="element-635" data-mp="frri1kefl51s">{m.value}</span>
                  {m.delta}
                </span>
                <span className="block text-xs text-muted" data-id="element-636" data-mp="30k36s3phzifv">{m.label}</span>
              </span>)}
          </span>

          <motion.span animate={{
          rotate: expanded ? 180 : 0
        }} transition={{
          duration: 0.22,
          ease
        }} className="order-2 ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted lg:order-none" aria-hidden data-id="element-637" data-mp="2s18584irpcy1">
            <ChevronDownIcon className="h-4 w-4" data-id="element-638" data-mp="2x2nte5g8rzkn" />
          </motion.span>
        </button>
      </h2>

      <AnimatePresence initial={false} data-id="element-639" data-mp="29d34l5xeq83g">
        {expanded && <motion.div id={panelId} key="panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        height: {
          duration: 0.26,
          ease
        },
        opacity: {
          duration: 0.18
        }
      }} data-id="element-640" data-mp="24d8hzeko04o7">
            <div className="border-t border-line px-5 pb-5 pt-4 sm:px-6" data-id="element-641" data-mp="1qkscm74ajuk4">
              <LayoutGroup id={`roster-${id}`} data-id="element-642" data-mp="1lmqpnb3qvb3d">
                <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6" data-id="element-643" data-mp="1llxrgyiuptwu">
                  <AnimatePresence mode="popLayout" initial={false} data-id="element-644" data-mp="tu6mh505od6r">
                    {visible.map((row, i) => <UserMiniCard key={row.agent.id} row={row} floor={floorById.get(row.agent.id)} index={i} highlighted={matchedIds.has(row.agent.id)} onSelect={onSelect} data-id="element-645" data-mp="xv8o1shjp1bw" data-mpi={i} />)}
                  </AnimatePresence>
                  {more > 0 && <motion.li layout="position" transition={{
                layout: {
                  duration: 0.25,
                  ease
                }
              }} data-id="element-646" data-mp="1hl2kijv7g4mg">
                      <button type="button" onClick={() => onShowAll(id, true)} className="flex h-full min-h-[64px] w-full items-center justify-center rounded-xl border border-dashed border-line text-sm font-semibold text-accent transition-colors duration-150 ease-out hover:border-bright/40 hover:bg-bright/5 hover:text-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-bright" data-id="element-647" data-mp="1lk1qm7soa7fh">
                        +{more} more
                      </button>
                    </motion.li>}
                </ul>
              </LayoutGroup>

              {(onViewAll || canShowFewer) && <div className="mt-3 flex items-center justify-end gap-4" data-id="element-648" data-mp="2m53rn8a5o58">
                  {canShowFewer && <button type="button" onClick={() => onShowAll(id, false)} className="rounded-md text-xs font-medium text-muted transition-colors duration-150 ease-out hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-bright" data-id="element-649" data-mp="3tqvey71x7u51">
                      Show fewer
                    </button>}
                  {onViewAll && <button type="button" onClick={onViewAll} className="flex items-center gap-1 rounded-md text-xs font-semibold text-accent transition-colors duration-150 ease-out hover:text-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-bright" data-id="element-650" data-mp="2avblcvsql7n5">
                      {viewAllLabel}
                      <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden data-id="element-651" data-mp="1s3ipmtma98bq" />
                    </button>}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}